import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    FiBell,
    FiMail,
    FiMessageSquare,
    FiSmartphone,
    FiSave,
    FiCheckCircle,
    FiAlertCircle,
    FiClock,
} from "react-icons/fi";

export default function AlertSettings() {
    const [alertSettings, setAlertSettings] = useState({
        email: {
            enabled: true,
            newCall: true,
            newTarget: true,
            newStopLoss: true,
            newReport: true,
            monthlyDigest: true,
        },
        sms: {
            enabled: false,
            newCall: false,
            newTarget: false,
            newStopLoss: true,
            newReport: false,
            monthlyDigest: false,
        },
        whatsapp: {
            enabled: false,
            optInConsent: false,
            newCall: false,
            newTarget: false,
            newStopLoss: true,
            newReport: false,
            monthlyDigest: false,
        },
        push: {
            enabled: true,
            newCall: true,
            newTarget: true,
            newStopLoss: true,
            newReport: true,
            monthlyDigest: false,
        },
        digestSchedule: "weekly", // daily, weekly, monthly
        dndEnabled: false,
        dndStartTime: "22:00",
        dndEndTime: "08:00",
    });

    const [saveStatus, setSaveStatus] = useState(null); // null, saving, success, error

    const handleToggle = (channel, setting) => {
        setAlertSettings((prev) => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                [setting]: !prev[channel][setting],
            },
        }));
    };

    const handleChannelToggle = (channel) => {
        setAlertSettings((prev) => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                enabled: !prev[channel].enabled,
            },
        }));
    };

    const handleWhatsAppOptIn = () => {
        if (window.confirm("Do you consent to receive WhatsApp alerts? You can opt-out anytime.")) {
            setAlertSettings((prev) => ({
                ...prev,
                whatsapp: {
                    ...prev.whatsapp,
                    optInConsent: true,
                    enabled: true,
                },
            }));
        }
    };

    const handleSave = () => {
        setSaveStatus("saving");
        // Simulate API call
        setTimeout(() => {
            setSaveStatus("success");
            setTimeout(() => setSaveStatus(null), 3000);
        }, 1000);
    };

    const channels = [
        {
            id: "email",
            name: "Email",
            icon: FiMail,
            description: "Receive alerts via email",
            color: "#1bafb4",
        },
        {
            id: "sms",
            name: "SMS",
            icon: FiMessageSquare,
            description: "Receive alerts via SMS",
            color: "#06b6a4",
        },
        {
            id: "whatsapp",
            name: "WhatsApp",
            icon: FiSmartphone,
            description: "Receive alerts via WhatsApp",
            color: "#10b981",
            requiresConsent: true,
        },
        {
            id: "push",
            name: "Push Notifications",
            icon: FiBell,
            description: "Receive push notifications",
            color: "#3b82f6",
        },
    ];

    const alertTypes = [
        { id: "newCall", label: "New Research Call", description: "Get notified when a new research call is published" },
        { id: "newTarget", label: "Target Price Update", description: "Get notified when target prices are updated" },
        { id: "newStopLoss", label: "Stop Loss Update", description: "Get notified when stop loss levels change" },
        { id: "newReport", label: "New Report", description: "Get notified when new research reports are available" },
        { id: "monthlyDigest", label: "Monthly Digest", description: "Receive monthly summary of all research updates" },
    ];

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div>
                                    <h3 className="text-primary mb-2">Research Alerts Settings</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Research Analysis</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item active">Alert Settings</li>
                                        </ol>
                                    </nav>
                                   
                                </div>
                                <button
                                    className="btn btn-primary "
                                    onClick={handleSave}
                                    disabled={saveStatus === "saving"}
                               
                                >
                                    {saveStatus === "saving" ? (
                                        <>
                                            {/* <FiClock className="me-2" /> */}
                                            Saving...
                                        </>
                                    ) : saveStatus === "success" ? (
                                        <>
                                            {/* <FiCheckCircle className="me-2" /> */}
                                            Saved
                                        </>
                                    ) : (
                                        <>
                                            {/* <FiSave className="me-2" /> */}
                                            Save Preferences
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Alert Channels */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-header border-0 d-flex align-items-center py-3 px-4"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                borderBottom: "1px solid #b3eaf4",
                            }}
                        >
                            <div
                                className="me-3 d-flex align-items-center justify-content-center"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "10px",
                                    background: "#c8eeecbf",
                                }}
                            >
                                <FiBell style={{ color: "#39b2b4", fontSize: "20px" }} />
                            </div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Alert Channels</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-4">
                                {channels.map((channel) => {
                                    const Icon = channel.icon;
                                    const channelSettings = alertSettings[channel.id];
                                    const isEnabled = channelSettings?.enabled;

                                    return (
                                        <div key={channel.id} className="col-lg-6">
                                            <div
                                                className="card border-0 h-100 shadow-sm"
                                                style={{
                                                    borderRadius: "12px",
                                                    border: `2px solid ${isEnabled ? "#39b2b4" : "#e9ecef"}`,
                                                    backgroundColor: isEnabled ? "#f0f9ff" : "#ffffff",
                                                    transition: "all 0.3s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = "translateY(-2px)";
                                                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.1)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = "translateY(0)";
                                                    e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
                                                }}
                                            >
                                                <div className="card-body p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="me-3 d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: "48px",
                                                                    height: "48px",
                                                                    borderRadius: "12px",
                                                                    background: isEnabled ? "#ccf1f7" : "#f1f5f9",
                                                                    border: `1px solid ${isEnabled ? "#b3eaf4" : "#e9ecef"}`,
                                                                }}
                                                            >
                                                                <Icon style={{ color: isEnabled ? "#39b2b4" : "#94a3b8", fontSize: "24px" }} />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-1 fw-bold" style={{ color: "#006e82", fontSize: "16px" }}>
                                                                    {channel.name}
                                                                </h6>
                                                                <p className="small mb-0" style={{ color: "#64748b", fontSize: "13px" }}>
                                                                    {channel.description}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                checked={isEnabled}
                                                                onChange={() => handleChannelToggle(channel.id)}
                                                                style={{
                                                                    width: "48px",
                                                                    height: "24px",
                                                                    cursor: "pointer",
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {channel.requiresConsent && channel.id === "whatsapp" && !channelSettings.optInConsent && (
                                                        <div
                                                            className="alert mb-3 py-3 border-0"
                                                            style={{
                                                                borderRadius: "10px",
                                                                fontSize: "13px",
                                                                backgroundColor: "#fef3c7",
                                                                border: "1px solid #fbbf24",
                                                                color: "#92400e",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-start">
                                                                <FiAlertCircle className="me-2 mt-1" style={{ fontSize: "18px", color: "#f59e0b" }} />
                                                                <div className="flex-grow-1">
                                                                    <strong style={{ fontSize: "14px" }}>Consent Required</strong>
                                                                    <div className="mt-2" style={{ fontSize: "12px" }}>
                                                                        You need to provide consent to receive WhatsApp alerts.
                                                                    </div>
                                                                    <button
                                                                        className="btn btn-sm mt-2 fw-semibold"
                                                                        onClick={handleWhatsAppOptIn}
                                                                        style={{
                                                                            borderRadius: "8px",
                                                                            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                                                            color: "white",
                                                                            border: "none",
                                                                        }}
                                                                    >
                                                                        Provide Consent
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {isEnabled && (
                                                        <div className="mt-4 pt-3 border-top" style={{ borderColor: "#b3eaf4" }}>
                                                            <h6 className="fw-semibold mb-3" style={{ fontSize: "14px", color: "#006e82" }}>
                                                                Alert Types:
                                                            </h6>
                                                            {alertTypes.map((alertType) => (
                                                                <div
                                                                    key={alertType.id}
                                                                    className="d-flex justify-content-between align-items-center mb-3 pb-3"
                                                                    style={{ borderBottom: "1px solid #e9ecef" }}
                                                                >
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-semibold small" style={{ color: "#006e82", fontSize: "13px" }}>
                                                                            {alertType.label}
                                                                        </div>
                                                                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                                                                            {alertType.description}
                                                                        </div>
                                                                    </div>
                                                                    <div className="form-check form-switch ms-3">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={channelSettings[alertType.id] || false}
                                                                            onChange={() => handleToggle(channel.id, alertType.id)}
                                                                            disabled={!isEnabled}
                                                                            style={{
                                                                                width: "40px",
                                                                                height: "20px",
                                                                                cursor: isEnabled ? "pointer" : "not-allowed",
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Digest Schedule */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-header border-0 d-flex align-items-center py-3 px-4"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                borderBottom: "1px solid #b3eaf4",
                            }}
                        >
                            <div
                                className="me-3 d-flex align-items-center justify-content-center"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "10px",
                                    background: "#c8eeecbf",
                                }}
                            >
                                <FiClock style={{ color: "#39b2b4", fontSize: "20px" }} />
                            </div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Digest Schedule</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                        Monthly Digest Frequency
                                    </label>
                                    <select
                                        className="form-select"
                                        value={alertSettings.digestSchedule}
                                        onChange={(e) =>
                                            setAlertSettings((prev) => ({
                                                ...prev,
                                                digestSchedule: e.target.value,
                                            }))
                                        }
                                        style={{
                                            borderRadius: "10px",
                                            borderColor: "#b3eaf4",
                                            fontSize: "14px",
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = "#006e82";
                                            e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = "#b3eaf4";
                                            e.target.style.boxShadow = "none";
                                        }}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                    <div className="mt-2" style={{ fontSize: "13px", color: "#64748b" }}>
                                        Choose how often you want to receive the monthly digest summary.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Do Not Disturb */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div
                            className="card-header border-0 d-flex align-items-center py-3 px-4"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                borderBottom: "1px solid #b3eaf4",
                            }}
                        >
                            <div
                                className="me-3 d-flex align-items-center justify-content-center"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "10px",
                                    background: "#c8eeecbf",
                                }}
                            >
                                <FiClock style={{ color: "#39b2b4", fontSize: "20px" }} />
                            </div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Do Not Disturb</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h6 className="fw-semibold mb-1" style={{ color: "#006e82", fontSize: "16px" }}>
                                        Enable Do Not Disturb
                                    </h6>
                                    <p className="small mb-0" style={{ color: "#64748b", fontSize: "13px" }}>
                                        Respect quiet hours and avoid sending alerts during specified times.
                                    </p>
                                </div>
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={alertSettings.dndEnabled}
                                        onChange={() =>
                                            setAlertSettings((prev) => ({
                                                ...prev,
                                                dndEnabled: !prev.dndEnabled,
                                            }))
                                        }
                                        style={{
                                            width: "48px",
                                            height: "24px",
                                            cursor: "pointer",
                                        }}
                                    />
                                </div>
                            </div>

                            {alertSettings.dndEnabled && (
                                <div className="row g-3 pt-3 border-top" style={{ borderColor: "#b3eaf4" }}>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                            Start Time
                                        </label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={alertSettings.dndStartTime}
                                            onChange={(e) =>
                                                setAlertSettings((prev) => ({
                                                    ...prev,
                                                    dndStartTime: e.target.value,
                                                }))
                                            }
                                            style={{
                                                borderRadius: "10px",
                                                borderColor: "#b3eaf4",
                                                fontSize: "14px",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = "#006e82";
                                                e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = "#b3eaf4";
                                                e.target.style.boxShadow = "none";
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2" style={{ color: "#006e82" }}>
                                            End Time
                                        </label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={alertSettings.dndEndTime}
                                            onChange={(e) =>
                                                setAlertSettings((prev) => ({
                                                    ...prev,
                                                    dndEndTime: e.target.value,
                                                }))
                                            }
                                            style={{
                                                borderRadius: "10px",
                                                borderColor: "#b3eaf4",
                                                fontSize: "14px",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = "#006e82";
                                                e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = "#b3eaf4";
                                                e.target.style.boxShadow = "none";
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Save Status */}
                    {saveStatus === "success" && (
                        <div
                            className="alert border-0"
                            style={{
                                borderRadius: "12px",
                                backgroundColor: "#d1fae5",
                                border: "1px solid #10b981",
                                color: "#065f46",
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <FiCheckCircle className="me-3" style={{ fontSize: "24px", color: "#10b981" }} />
                                <div>
                                    <strong style={{ fontSize: "16px", color: "#065f46" }}>Preferences Saved</strong>
                                    <div className="mt-2" style={{ fontSize: "14px", color: "#065f46", opacity: 0.9 }}>
                                        Your alert preferences have been updated successfully.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info Notice */}
                    <div
                        className="alert border-0"
                        style={{
                            borderRadius: "12px",
                            backgroundColor: "#ccf1f7",
                            border: "1px solid #b3eaf4",
                            color: "#006e82",
                        }}
                    >
                        <div className="d-flex align-items-start">
                            <FiAlertCircle className="me-3 mt-1" style={{ fontSize: "24px", color: "#006e82" }} />
                            <div>
                                <strong style={{ fontSize: "16px", color: "#006e82" }}>Note:</strong>
                                <ul className="mb-0 mt-2" style={{ paddingLeft: "20px", fontSize: "14px", color: "#006e82", opacity: 0.9 }}>
                                    <li>Preference changes are logged for audit purposes</li>
                                    <li>WhatsApp alerts require explicit opt-in consent</li>
                                    <li>Do Not Disturb settings apply to all alert channels</li>
                                    <li>You can modify these settings at any time</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
          
        </>
    );
}
